import { useEffect, useRef } from 'react';
import { invokeEdgeFunction } from '@/lib';
import type { MemberDirectoryEntry } from '@/types';

/**
 * NFC check-in at reception.
 *
 * The desk's Apple VAS reader (e.g. Dot Origin VTAP100) decrypts a member's
 * Wallet tap and "types" the card's token followed by Enter, like a keyboard.
 * This listens for that burst anywhere in hok-os and hands the token to
 * `onToken`; the host resolves it (resolve-nfc-token) and opens the normal
 * check-in modal.
 *
 * A reader types far faster than a person, so a token only counts if every
 * key arrived within MAX_KEY_GAP_MS of the previous one — a human typing
 * "HK1." into a search box never triggers it. Keys are observed, not
 * swallowed: if a text field has focus the token also lands there, so the
 * reception screen is best left with nothing focused.
 */

/** Wallet card NFC token: HK1.<22 base64url>.<22 base64url> (see _shared/wallet-pass/nfc.ts). */
export const NFC_TOKEN_PATTERN = /^HK1\.[A-Za-z0-9_-]{22}\.[A-Za-z0-9_-]{22}$/;

/** Readers emit a key every few ms; people are ≥ 50 ms apart. */
export const MAX_KEY_GAP_MS = 35;

const MAX_BUFFER = 64;

/**
 * Pure key-stream detector: feed each key with its timestamp; returns the
 * token when Enter completes a fast, well-formed burst, else null.
 */
export function createNfcKeyBuffer(maxGapMs = MAX_KEY_GAP_MS) {
  let buffer = '';
  let last = -Infinity;

  return (key: string, at: number): string | null => {
    const gap = at - last;
    last = at;

    if (key === 'Enter') {
      const candidate = buffer;
      buffer = '';
      return gap <= maxGapMs && NFC_TOKEN_PATTERN.test(candidate) ? candidate : null;
    }
    if (key.length !== 1) return null; // Shift, arrows… don't reset the burst

    buffer = gap <= maxGapMs ? (buffer + key).slice(-MAX_BUFFER) : key;
    return null;
  };
}

export const NFC_MESSAGES = {
  unknown: "This card isn't recognised. Check the member in by name.",
  notInDirectory: 'Card read, but the member is not in the list yet. Refresh the page and tap again.',
  busy: 'Finish the open check-in first, then tap again.',
} as const;

export type NfcResolution =
  | { ok: true; member: MemberDirectoryEntry }
  | { ok: false; message: string };

/** Token → member via resolve-nfc-token (the signing secret is server-only). */
export async function resolveNfcCheckIn(
  token: string,
  members: MemberDirectoryEntry[] | undefined,
): Promise<NfcResolution> {
  const { data, error } = await invokeEdgeFunction<{ member_id?: string }>('resolve-nfc-token', {
    body: { token },
  });
  if (error || !data?.member_id) return { ok: false, message: NFC_MESSAGES.unknown };
  const member = members?.find((m) => m.id === data.member_id);
  return member ? { ok: true, member } : { ok: false, message: NFC_MESSAGES.notInDirectory };
}

export function useNfcCheckInReader(enabled: boolean, onToken: (token: string) => void) {
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!enabled) return;
    const feed = createNfcKeyBuffer();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Chrome autofill (and some password managers) dispatch a bare `keydown`
      // Event with no `key` when a saved suggestion is picked — not a reader.
      if (typeof event.key !== 'string') return;
      const token = feed(event.key, event.timeStamp || performance.now());
      if (!token) return;
      // The reader's closing Enter must not also press whatever is focused —
      // e.g. an open check-in modal's "Check in" button.
      event.preventDefault();
      event.stopPropagation();
      onTokenRef.current(token);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled]);
}

/**
 * Reader → resolved member, safe against the races a busy desk produces:
 *   - a tap while a check-in modal is open is refused (`busy`), never swapped in;
 *   - only the latest tap's answer counts — a slow earlier lookup can't
 *     overwrite a later member, and a modal opened meanwhile (header search)
 *     wins over any lookup still in flight.
 */
export function useNfcCheckIn({
  enabled,
  members,
  busy,
  onMember,
  onMessage,
}: {
  enabled: boolean;
  members: MemberDirectoryEntry[] | undefined;
  /** True while a check-in modal is open. */
  busy: boolean;
  onMember: (member: MemberDirectoryEntry) => void;
  onMessage: (message: string, type: 'error' | 'info') => void;
}) {
  const latest = useRef({ members, busy, onMember, onMessage });
  latest.current = { members, busy, onMember, onMessage };
  const scanId = useRef(0);

  useNfcCheckInReader(enabled, (token) => {
    if (latest.current.busy) {
      latest.current.onMessage(NFC_MESSAGES.busy, 'info');
      return;
    }
    const scan = ++scanId.current;
    void resolveNfcCheckIn(token, latest.current.members).then((result) => {
      if (scan !== scanId.current || latest.current.busy) return; // superseded
      if (result.ok) latest.current.onMember(result.member);
      else latest.current.onMessage(result.message, 'error');
    });
  });
}
